import { useCallback, useEffect, useState } from "react";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import {
  ChambreStat,
  Statistiques,
  statistiquesService,
} from "../services/statistiques.service";
import "../styles/StatistiquesPage.css";

/**
 * Audience du site public d'une colocation.
 *
 * L'écran est construit autour d'une question, pas autour de compteurs :
 * « mon problème est-il le trafic, ou l'annonce ? ». D'où l'entonnoir en
 * premier, les taux de passage affichés à côté des volumes, et le classement
 * des chambres qui inclut celles à zéro vue — une chambre que personne ne
 * regarde est l'information la plus utile de la page.
 */

const PERIODES = [
  { jours: 7, libelle: "7 jours" },
  { jours: 30, libelle: "30 jours" },
  { jours: 90, libelle: "90 jours" },
];

function pourcentage(numerateur: number, denominateur: number): string {
  if (!denominateur) return "—";
  return `${Math.round((numerateur / denominateur) * 100)} %`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const StatistiquesPage: React.FC = () => {
  const colocSelectionnee = useSelectedColoc();
  const [jours, setJours] = useState(30);
  const [stats, setStats] = useState<Statistiques | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    if (!colocSelectionnee) return;
    setChargement(true);
    setErreur(null);
    try {
      setStats(await statistiquesService.get(colocSelectionnee.id, jours));
    } catch {
      setErreur("Les statistiques n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, [colocSelectionnee, jours]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!colocSelectionnee) {
    return (
      <div className="statistiques">
        <p className="statistiques-vide">
          Sélectionnez une colocation pour voir son audience.
        </p>
      </div>
    );
  }

  return (
    <div className="statistiques">
      <div className="statistiques-entete">
        <h2>Audience du site</h2>
        <div className="statistiques-periodes" role="group" aria-label="Période">
          {PERIODES.map((p) => (
            <button
              key={p.jours}
              type="button"
              className={p.jours === jours ? "actif" : ""}
              onClick={() => setJours(p.jours)}
            >
              {p.libelle}
            </button>
          ))}
        </div>
      </div>

      {erreur && <p className="statistiques-erreur">{erreur}</p>}
      {chargement && !stats && <p className="statistiques-vide">Chargement…</p>}

      {stats && (
        <>
          {/* Sans cet avertissement, une période antérieure au déploiement
              affiche « 0 vue » à côté de contacts bien réels — ce qui se lit
              comme une panne. */}
          {stats.mesureDepuis === null ? (
            <p className="statistiques-avis">
              L&apos;audience de ce site n&apos;a pas encore été mesurée. Les
              chiffres apparaîtront dès les premières visites.
            </p>
          ) : (
            <p className="statistiques-avis">
              Audience mesurée depuis le {formatDate(stats.mesureDepuis)}. Les
              contacts et candidatures antérieurs à cette date sont comptés,
              mais pas les visites qui les ont précédés.
            </p>
          )}

          <section className="statistiques-entonnoir">
            <Etape valeur={stats.vues.total} libelle="pages vues" />
            <Fleche taux={pourcentage(stats.contacts, stats.vues.total)} />
            <Etape valeur={stats.contacts} libelle="contacts" />
            <Fleche taux={pourcentage(stats.candidatures, stats.contacts)} />
            <Etape valeur={stats.candidatures} libelle="candidatures" />
          </section>

          <section className="statistiques-cartes">
            <Carte
              valeur={stats.visiteurs}
              libelle="visiteurs distincts"
              precision="Comptés par jour : une même personne revenue deux jours compte deux fois."
            />
            <Carte valeur={stats.vues.coloc} libelle="vues de la page d'accueil" />
            <Carte valeur={stats.vues.chambres} libelle="vues de pages de chambre" />
          </section>

          <section>
            <h3>Chambres</h3>
            <div className="statistiques-tableau-cadre">
              <table className="statistiques-tableau">
                <thead>
                  <tr>
                    <th>Chambre</th>
                    <th>Vues</th>
                    <th>Contacts</th>
                    <th>Candidatures</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.chambres.map((c) => (
                    <LigneChambre key={c.roomId} chambre={c} />
                  ))}
                  {stats.chambres.length === 0 && (
                    <tr>
                      <td colSpan={4} className="statistiques-vide">
                        Aucune chambre dans cette colocation.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

const Etape: React.FC<{ valeur: number; libelle: string }> = ({ valeur, libelle }) => (
  <div className="statistiques-etape">
    <strong>{valeur}</strong>
    <span>{libelle}</span>
  </div>
);

const Fleche: React.FC<{ taux: string }> = ({ taux }) => (
  <div className="statistiques-fleche" aria-hidden="false">
    <span className="statistiques-taux">{taux}</span>
  </div>
);

const Carte: React.FC<{ valeur: number; libelle: string; precision?: string }> = ({
  valeur,
  libelle,
  precision,
}) => (
  <div className="statistiques-carte">
    <strong>{valeur}</strong>
    <span>{libelle}</span>
    {precision && <small>{precision}</small>}
  </div>
);

const LigneChambre: React.FC<{ chambre: ChambreStat }> = ({ chambre }) => {
  // Le signal actionnable : la chambre est vue mais ne déclenche rien. Le
  // problème n'est alors ni la visibilité ni le site, mais l'annonce
  // elle-même — prix, photos, description.
  const vueSansSuite = chambre.vues >= 10 && chambre.contacts === 0;
  return (
    <tr>
      <td>
        {chambre.nom}
        {vueSansSuite && (
          <span className="statistiques-signal" title="Vue, mais aucun contact">
            aucun contact
          </span>
        )}
      </td>
      <td>{chambre.vues}</td>
      <td>{chambre.contacts}</td>
      <td>{chambre.candidatures}</td>
    </tr>
  );
};

export default StatistiquesPage;
